/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author jayah
 * 06/11/2024
 *
 */


import React from 'react';
import FormElementGenerator from "../../form-elements/formElementGenerator";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";

/**
 * HOC for the federations form
 */
class FederationsFilteringForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            fedUserMappingId: null,
            fedExternalUserId: '',
            fedIesUserId: '',
            fedIdpUserId: '',
            fedSmsId: '',
            fedPartnerId: '',
            fedInstitutionId: '',
            fedToolProxyGuid: '',
            fedCreatedDate: '',
            isSMSLinked: false,
            isIESLinked: false,
            isDisplayLoader: true,
            errors: {},

            // form fields
            fields: [
                {
                    inputType: 'text',
                    labelName: 'External User ID:',
                    inputName: 'fedExternalUserId',
                    placeholder: 'Please enter the external user ID here',
                    value: '',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'IES User ID:',
                    inputName: 'fedIesUserId',
                    placeholder: 'Please enter the IES user ID here',
                    value: '',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'IDP User ID:',
                    inputName: 'fedIdpUserId',
                    placeholder: 'Please enter the IDP user ID here',
                    value: '',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'SMS ID:',
                    inputName: 'fedSmsId',
                    placeholder: 'Please enter the SMS ID here',
                    value: '',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'Partner ID:',
                    inputName: 'fedPartnerId',
                    placeholder: 'Please enter the partner ID here',
                    value: '',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'Institution ID:',
                    inputName: 'fedInstitutionId',
                    placeholder: 'Please enter the institution ID here',
                    value: '',
                    required: false
                },
                {
                    inputType: 'text',
                    labelName: 'Tool Proxy GUID:',
                    inputName: 'fedToolProxyGuid',
                    placeholder: 'Please enter the tool proxy GUID here',
                    value: '',
                    required: false
                },
                {
                    inputType: 'dateTime',
                    labelName: 'Created Date:',
                    inputName: 'fedCreatedDate',
                    optionValueName: 'fedCreatedDate',
                    optionLabelName: 'fedCreatedDate',
                    valueType: 'startDate',
                    syncInitialValue: false,
                    placeholder: 'Please enter the created date here',
                    value: '',
                    required: false
                }
            ]
        };
        EventEmitter.emit('setPageHeaderData', {
            page: 'federations',
            pageName: "View Federation",
            pageLink: process.env.REACT_APP_BASE_URL + "/#/home/federations",
            optionalIcons: ['manageColumnsIcon']
        });
    }

    componentDidMount() {
    }

    /**
     * update the state when user input a value
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionLabelName]: event.label,
                [event.optionValueName]: event.value
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            });
        }
    };

    loadFilteredResults = () => {
        let search = '&search=%7B';

        // get required fields
        for (const [key, value] of Object.entries(this.state)) {
            switch (key) {
                case 'fedExternalUserId':
                case 'fedIesUserId':
                case 'fedIdpUserId':
                case 'fedSmsId':
                case 'fedPartnerId':
                case 'fedInstitutionId':
                case 'fedToolProxyGuid':
                case 'fedCreatedDate':
                case 'isSMSLinked':
                case 'isIESLinked':
                    if ((typeof value === "string" && value !== '') || (typeof value === "boolean" && value)) {
                        search = search.concat(key, ':', value, '%2C');
                    }
                    break;
                default:
            }
        }

        // remove the last %2C and add to the url
        if (search.length === 11) {
            search = '';
        } else if (search.length > 10) {
            search = search.slice(0, -3).concat('%7D');
        } else {
            search = '';
        }

        // publish the url to the table component
        EventEmitter.emit('tableFilterUrl', search);
        EventEmitter.emit('openNav', '');
    };

    clearFilteredResults = () => {
        const newState = Object.assign({}, this.state);

        newState.fedExternalUserId = '';
        newState.fedIesUserId = '';
        newState.fedIdpUserId = '';
        newState.fedSmsId = '';
        newState.fedPartnerId = '';
        newState.fedInstitutionId = '';
        newState.fedToolProxyGuid = '';
        newState.fedCreatedDate = '';
        newState.isSMSLinked = false;
        newState.isIESLinked = false;

        newState.errors = {};

        this.setState(newState);

        EventEmitter.emit('tableFilterUrl', '');
        EventEmitter.emit('openNav', '');
    };

    /**
     * fire alert
     * @param alertType
     * @param alertTitle
     * @param alertMessage
     */
    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    render() {
        return (
            <div className="view-error-filter-container">
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
                <div className="view-audit-filter-button-container">
                    <input type="button" className="view-audit-filter-cancel" value="Clear"
                           onClick={() => this.clearFilteredResults()} />
                    <input type="button" className="view-audit-filter-submit" value="Filter"
                           onClick={() => this.loadFilteredResults()} />
                </div>
            </div>
        );
    }
}

export default FederationsFilteringForm;

